import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import type { LocalSettingsState } from '@ps/web-core-store';
import { CashierPath } from '@ps/web-pam-types';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef } from 'react';
import BodyClassName from 'react-body-classname';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { Switch, withRouter } from 'react-router';
import { Route } from 'react-router-dom';
import type { DeepPartial } from 'redux';
import URL from 'url-parse';

import { ChestInventory } from '@swf/account';
import { CommunityForumLogin, PSNAuthorizationCallback } from '@swf/auth';
import CasinoGameLaunch from '@swf/components/CasinoGameLaunch/CasinoGameLaunch';
import EmbeddedCSAT from '@swf/components/EmbeddedCSAT/EmbeddedCSAT';
import EmbeddedCookieConsent from '@swf/components/EmbeddedCookieConsent/EmbeddedCookieConsent';
import EmbeddedFooter from '@swf/components/EmbeddedFooter/EmbeddedFooter';
import ErrorBoundary from '@swf/components/ErrorBoundary/ErrorBoundary';
import ServiceUnavailable from '@swf/components/ServiceUnavailable/ServiceUnavailable';
import StarsModalRoutes from '@swf/components/StarsModalRoutes/StarsModalRoutes';
import StarsPage from '@swf/components/StarsPage/StarsPage';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { PartnerNotification } from '@swf/notification';

import AccountC2LRoute from './components/AccountC2LRoute';
import DepositRoute from './components/DepositRoute';
import ManageCurrenciesRoute from './components/ManageCurrenciesRoute';
import ModalRoute from './components/ModalRoute';
import P2pTransferRoute from './components/P2pTransferRoute';
import PaymentMethodsRoute from './components/PaymentMethodsRoute';
import RmfRoute from './components/RmfRoute';
import SignupRoute from './components/SignupRoute';
import TransactionHistoryRoute from './components/TransactionHistoryRoute';
import WithdrawRoute from './components/WithdrawRoute';

type Props = RouteComponentProps<Record<string, string>>;

const Routes: React.FC<Props> = ({ location, staticContext = {} }) => {
  const dispatch = useDispatch<Dispatch>();
  const embeddedModal = useSelector(select.location.isEmbeddedModal);
  const embeddedAccount = useSelector(select.location.isEmbeddedAccount);
  const isEmbeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const pathname = useSelector(select.location.getPathname);
  const hasClientSession = useSelector(select.session.hasClientSession);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isCMSAvailable = useSelector(select.system.isCMSAvailable);
  const locale = useSelector(select.locale.get);
  const favicon = useSelector(select.cms.getFaviconURL);
  const prevSearchParams = useRef(location.search);
  const embeddedModalRoute = embeddedModal || embeddedAccount;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const brand = searchParams.get('brand');
    const redirectTo = searchParams.get('redirectTo');
    const settings: DeepPartial<LocalSettingsState> = {};

    if (brand && BrandTypes[parseInt(brand)]) settings.registrationBrand = parseInt(brand);
    if (redirectTo) settings.redirectTo = redirectTo.replace(/^\/+/g, '/');
    if (!isEmpty(settings)) dispatch.localSettings.set(settings);
  }, [location, dispatch.location, dispatch.localSettings]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (
      location.pathname !== pathname ||
      location.search !== prevSearchParams.current ||
      searchParams.has('modal')
    ) {
      dispatch.location.update(new URL(window.location.href, true));
      prevSearchParams.current = location.search;
    }
  }, [dispatch.location, location, pathname]);

  if (!isCMSAvailable) {
    staticContext.statusCode = 503;
    return <ServiceUnavailable />;
  }

  return (
    <ErrorBoundary fallback={<ServiceUnavailable />}>
      <Helmet bodyAttributes={{ translate: 'no' }}>
        <html lang={locale} data-renderer={hasClientSession ? 'client' : 'server'} />
        <meta name="google" content="notranslate" />
        <link rel="icon" href={favicon} />
      </Helmet>
      <BodyClassName className="notranslate" />
      <Switch>
        <Route path="/casino/launch/:type(game|race)/:id?" component={CasinoGameLaunch} />
        {isEmbeddedRoute && <Route path="/chest-inventory" component={ChestInventory} />}
        {isEmbeddedRoute && <Route path="/footer" component={EmbeddedFooter} />}
        {isEmbeddedRoute && <Route path="/cookie-consent" component={EmbeddedCookieConsent} />}
        {isEmbeddedRoute && <Route path="/touchpoint/:param" component={EmbeddedCSAT} />}
        {isEmbeddedRoute && (
          <Route path="/notifications/psn-partner" component={PartnerNotification} />
        )}
        <Route path="/login" render={() => <ModalRoute modal={{ path: '/auth/login' }} />} />
        <Route
          path="/redirected"
          render={() => <ModalRoute modal={{ path: '/notifications/redirected' }} />}
        />
        <Route
          path="/docverify"
          render={() =>
            isAuthenticated ? (
              <ModalRoute soft modal={{ path: '/auth/docverify' }} />
            ) : (
              <ModalRoute
                modal={{ path: '/auth/login', locationState: { path: '/auth/docverify' } }}
              />
            )
          }
        />
        <Route path={CashierPath.RealMoneyForm} component={RmfRoute} />
        <Route path={CashierPath.Deposit} component={DepositRoute} />
        <Route path={CashierPath.Withdraw} component={WithdrawRoute} />
        <Route path={CashierPath.Signup} component={SignupRoute} />
        <Route path={CashierPath.P2pTransfer} component={P2pTransferRoute} />
        <Route path={CashierPath.TransactionHistory} component={TransactionHistoryRoute} />
        <Route path={CashierPath.ManageCurrencies} component={ManageCurrenciesRoute} />
        <Route path={CashierPath.PaymentMethods} component={PaymentMethodsRoute} />
        <Route path="/auth/psn-authorization-callback" component={PSNAuthorizationCallback} />
        {!embeddedModalRoute && (
          <Route
            path="/account"
            render={({ location: { pathname: path } }) => <ModalRoute modal={{ path }} />}
          />
        )}
        {!embeddedModalRoute && (
          <Route
            path="/prhub"
            render={({ location: { pathname: path } }) => <ModalRoute modal={{ path }} />}
          />
        )}
        <Route path="/account/c2l" component={AccountC2LRoute} />
        <Route path="/community/login" component={CommunityForumLogin} />
        <Route
          render={() =>
            embeddedModalRoute ? <StarsModalRoutes setStyle={() => undefined} /> : <StarsPage />
          }
        />
      </Switch>
    </ErrorBoundary>
  );
};

export default withRouter(Routes);
